<div class="container-fluid py-3">
    <h4><i class="fa-solid fa-bullseye"></i> Google Ads Conversion Attribution</h4>
    <p class="text-muted mb-3">
        Offline conversions queued for upload to Google Ads via the Data Manager API.
        Each row represents an eligible subscription activation matched using a click ID or hashed email.
        Processed means Google finished ingestion; attribution and campaign credit are reported separately in Google Ads.
    </p>

    <!-- Stats cards -->
    <div class="row mb-3">
        <div class="col-md-3 col-6 mb-2">
            <div class="card text-center">
                <div class="card-body py-2">
                    <h5 class="mb-0">{{ filteredConversions.length }}</h5>
                    <small class="text-muted">Loaded Records</small>
                </div>
            </div>
        </div>
        <div class="col-md-3 col-6 mb-2">
            <div class="card text-center">
                <div class="card-body py-2">
                    <h5 class="mb-0">R{{ totalValue | number:'1.2-2' }}</h5>
                    <small class="text-muted">Queued Value</small>
                </div>
            </div>
        </div>
        <div class="col-md-2 col-4 mb-2">
            <div class="card text-center border-warning">
                <div class="card-body py-2">
                    <h5 class="mb-0 text-warning">{{ pendingCount }}</h5>
                    <small class="text-muted">Pending</small>
                </div>
            </div>
        </div>
        <div class="col-md-2 col-4 mb-2">
            <div class="card text-center border-success">
                <div class="card-body py-2">
                    <h5 class="mb-0 text-success">{{ uploadedCount }}</h5>
                    <small class="text-muted">Processed</small>
                </div>
            </div>
        </div>
        <div class="col-md-2 col-4 mb-2">
            <div class="card text-center border-danger">
                <div class="card-body py-2">
                    <h5 class="mb-0 text-danger">{{ failedCount }}</h5>
                    <small class="text-muted">Failed</small>
                </div>
            </div>
        </div>
    </div>

    <p class="text-muted">{{ acceptedCount }} accepted / awaiting verification · {{ reviewCount }} with errors or warnings. Showing at most {{ maxRecords }} recent records.</p>
    <button class="btn btn-outline-primary btn-sm mb-2" [disabled]="loading" (click)="verifyUploads()">Check Google processing status</button>
    <p *ngIf="verificationMessage">{{ verificationMessage }}</p>

    <!-- Filters & Export -->
    <div class="d-flex justify-content-between align-items-center mb-2">
        <div class="btn-group btn-group-sm">
            <button class="btn" [class.btn-primary]="statusFilter === 'all'" [class.btn-outline-primary]="statusFilter !== 'all'" (click)="setFilter('all')">All</button>
            <button class="btn" [class.btn-warning]="statusFilter === 'pending'" [class.btn-outline-warning]="statusFilter !== 'pending'" (click)="setFilter('pending')">Pending</button>
            <button class="btn" [class.btn-success]="statusFilter === 'uploaded'" [class.btn-outline-success]="statusFilter !== 'uploaded'" (click)="setFilter('uploaded')">Processed</button>
            <button class="btn" [class.btn-danger]="statusFilter === 'failed'" [class.btn-outline-danger]="statusFilter !== 'failed'" (click)="setFilter('failed')">Failed</button>
            <button class="btn btn-outline-danger" (click)="setFilter('review')">Needs review</button>
        </div>
        <button (click)="exportGoogleAdsCsv()" class="btn btn-info btn-sm text-white px-3 py-1">
            <i class="fas fa-file-csv"></i> Export GCLID CSV
        </button>
    </div>

<p class="text-muted small">CSV export includes GCLID records only, with order IDs for deduplication. Email-only and braid conversions use the automated API.</p>

    <p *ngIf="loadError" class="text-danger">{{ loadError }}</p>

    <!-- Loading -->
    <div *ngIf="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
        </div>
    </div>

    <!-- Table -->
    <div *ngIf="!loading" class="table-responsive">
        <table class="table table-striped table-hover table-sm" id="ad-conversions-table">
            <thead>
                <tr>
                    <th>Activation</th>
                    <th>Status</th>
                    <th>Subscription</th>
                    <th>Value</th>
                    <th>Click ID</th>
                    <th>Email Hash</th>
                    <th>Source</th>
                    <th>Campaign</th>
                    <th>Processed</th>
                    <th>Google processing / diagnostics</th>
                </tr>
            </thead>
            <tbody>
                <tr *ngFor="let c of filteredConversions">
                    <td>{{ c.conversionTimestampDate | date:'dd MMM yyyy HH:mm' }}</td>
                    <td>
                        <span class="badge"
                            [class.bg-warning]="c.uploadStatus === 'pending'"
                            [class.bg-success]="c.processingStatus === 'SUCCESS'"
                            [class.bg-danger]="c.uploadStatus === 'failed' || c.uploadStatus === 'review'"
                            [class.bg-info]="c.uploadStatus === 'accepted' || (c.uploadStatus === 'uploaded' && !c.processingStatus)"
                        >{{ c.processingStatus === 'SUCCESS' ? 'processed' : c.uploadStatus === 'uploaded' ? 'unverified' : c.uploadStatus }}</span>
                        <span *ngIf="c.uploadAttempts > 1" class="badge bg-secondary ms-1">x{{ c.uploadAttempts }}</span>
                    </td>
                    <td>
                        <a [routerLink]="['/admin/subscription', c.subscriptionKey]" class="text-decoration-none">
                            {{ c.subscriptionKey | slice:0:8 }}...
                        </a>
                    </td>
                    <td class="text-end">R{{ c.conversionValueRands }}</td>
                    <td>
                        <span *ngIf="c.gclid" class="badge bg-primary" title="{{ c.gclid }}">gclid</span>
                        <span *ngIf="c.gbraid" class="badge bg-info" title="{{ c.gbraid }}">gbraid</span>
                        <span *ngIf="c.wbraid" class="badge bg-info" title="{{ c.wbraid }}">wbraid</span>
                        <span *ngIf="!c.gclid && !c.gbraid && !c.wbraid" class="text-muted">-</span>
                    </td>
                    <td>
                        <span *ngIf="c.clientEmailHash" class="badge bg-secondary" title="{{ c.clientEmailHash }}">SHA256</span>
                        <span *ngIf="!c.clientEmailHash" class="text-muted">-</span>
                    </td>
                    <td>{{ c.p_source || '-' }}</td>
                    <td>{{ c.p_campaign || '-' }}</td>
                    <td>{{ c.uploadedAtDate | date:'dd MMM HH:mm' }}</td>
                    <td>
                        <div>{{ c.processingStatus || 'Not checked' }}</div>
                        <div *ngIf="c.uploadRequestId" class="small text-muted" [title]="c.uploadRequestId">Request {{ c.uploadRequestId | slice:0:8 }}</div>
                        <div *ngIf="c.statusCheckError" class="text-danger small">{{ c.statusCheckError }}</div>
                        <details *ngIf="c.processingErrors?.length || c.processingWarnings?.length || c.uploadFieldWarnings?.length">
                            <summary>Batch diagnostics</summary><pre class="small">{{ c.diagnostics }}</pre>
                        </details>
                        <span *ngIf="c.uploadError" class="text-danger small" [title]="c.uploadError">
                            {{ c.uploadError | slice:0:50 }}{{ c.uploadError?.length > 50 ? '...' : '' }}
                        </span>
                    </td>
                </tr>
            </tbody>
        </table>
        <p *ngIf="filteredConversions.length === 0" class="text-muted text-center py-3">
            No conversions found for the selected filter.
        </p>
    </div>
</div>
